export interface PicadaAprendizajeIA {
  ID: string;
  FECHA: string;
  HORA: string;
  SENTIDO: string;
  OBRA: string;
}

export interface ContextoJornadaIA {
  ANTERIOR?: PicadaAprendizajeIA[];
  SIGUIENTE?: PicadaAprendizajeIA[];
}

export interface HuellaJornadaIA {
  CANTIDAD: number;
  PARIDAD: string;
  SECUENCIA: string[];
  MINUTOS: number[];
  INTERVALOS: number[];
  REPETIDOS: number[];
  ALTERNA: boolean;
  INICIO: number | null;
  FIN: number | null;
  AMPLITUD: number;
  TRAMOS_TRABAJADOS: number[];
  PAUSAS: number[];
}

export interface CambioAprendizajeIA {
  TIPO: string;
  POSICION_ANTES: number;
  POSICION_DESPUES: number;
  ANTES?: PicadaAprendizajeIA;
  DESPUES?: PicadaAprendizajeIA;
  INTERVALO_ANTERIOR?: number | null;
  INTERVALO_SIGUIENTE?: number | null;
}

export interface ReglaAprendizajeIA {
  VERSION: number;
  HUELLA_ANTES: HuellaJornadaIA;
  HUELLA_DESPUES: HuellaJornadaIA;
  CAMBIOS: CambioAprendizajeIA[];
  CONTEXTO: { ANTERIOR: HuellaJornadaIA; SIGUIENTE: HuellaJornadaIA; };
  TOLERANCIAS: {
    INTERVALO_PORCENTAJE: number;
    INTERVALO_MINIMO: number;
    INTERVALO_MAXIMO: number;
    AMPLITUD: number;
    HORA_INICIO: number;
    CONTEXTO_HORA: number;
  };
  CONDICIONES: string[];
  SEGURIDAD_MINIMA?: number;
  NOMBRE?: string;
  NOTA_USUARIO?: string;
  MANUAL?: boolean;
  CRITERIOS_MANUALES?: {
    MIN_PICADAS: number;
    MAX_PICADAS: number;
    INTERVALO_ERROR_MAX: number;
  };
}

export interface ComparacionAprendizajeIA {
  COINCIDE: boolean;
  SEGURIDAD: number;
  CUMPLE: string[];
  NO_CUMPLE: string[];
}

export interface CasoAprendizajeIA {
  ID: string;
  CLIENTE: string;
  CODIGO: string;
  NOMBRE: string;
  FECHA: string;
  CREADO: string;
  PATRON: string;
  EXPLICACION: string;
  ACCION: string;
  ANTES: PicadaAprendizajeIA[];
  DESPUES: PicadaAprendizajeIA[];
  REGLA: ReglaAprendizajeIA;
  INCIDENCIA?: any;
  MOSTRADO?: boolean;
  SERVIDOR_ID?: number;
  CONFIRMACIONES?: number;
  CONFIANZA?: number;
  ACIERTOS?: number;
  FALLOS?: number;
  APROBADA_MANUALMENTE?: boolean;
}

/** Memoria explicable, aprobada manualmente y aislada por cliente. */
export class AhoritaAprendizaje {
  private static clave(cliente: string): string {
    return 'ahorita:aprendizajes:v3:' + String(cliente || '').trim().toUpperCase();
  }

  static normalizar(picadas: any[]): PicadaAprendizajeIA[] {
    return (Array.isArray(picadas) ? picadas : []).map((p: any, indice: number) => ({
      ID: String(p && (p.ID || p.id) || ('nuevo-' + indice)),
      FECHA: String(p && (p.FECHA || p.fecha) || '').substr(0, 10),
      HORA: String(p && (p.HORA || p.hora || '') || '').substr(0, 5),
      SENTIDO: this.sentido(p && (p.SENTIDO || p.sentido)),
      OBRA: String(p && (p.OBRA || p.obra) || '').trim()
    })).filter(p => !!p.FECHA && this.minutos(p.HORA) !== null && !!p.SENTIDO)
      .sort((a, b) => (a.FECHA + ' ' + a.HORA + ' ' + a.ID).localeCompare(b.FECHA + ' ' + b.HORA + ' ' + b.ID));
  }

  /** Analiza el antes y el despues sin guardar ningun aprendizaje. */
  static proponer(cliente: string, codigo: string, nombre: string, fecha: string,
    antes: any[], despues: any[], incidencia?: any, contexto?: ContextoJornadaIA): CasoAprendizajeIA | null {
    const original = this.normalizar(antes);
    const corregido = this.normalizar(despues);
    if (this.iguales(original, corregido)) { return null; }
    const regla = this.crearRegla(original, corregido, contexto || {});
    const interpretacion = this.interpretar(regla);
    return {
      ID: String(Date.now()) + '-' + Math.floor(Math.random() * 100000),
      CLIENTE: String(cliente || ''), CODIGO: String(codigo || ''),
      NOMBRE: String(nombre || codigo || ''), FECHA: String(fecha || '').substr(0, 10),
      CREADO: new Date().toISOString(), PATRON: interpretacion.patron,
      EXPLICACION: interpretacion.explicacion, ACCION: interpretacion.accion,
      ANTES: original, DESPUES: corregido, REGLA: regla,
      INCIDENCIA: incidencia || null, MOSTRADO: false, CONFIANZA: 25, CONFIRMACIONES: 1
    };
  }

  static crearManual(cliente: string, nombre: string, patron: string, nota: string, criterios: any): CasoAprendizajeIA {
    const vacia = this.huella([]);
    const regla: ReglaAprendizajeIA = {
      VERSION: 3, HUELLA_ANTES: vacia, HUELLA_DESPUES: vacia, CAMBIOS: [],
      CONTEXTO: { ANTERIOR: vacia, SIGUIENTE: vacia },
      TOLERANCIAS: { INTERVALO_PORCENTAJE: 45, INTERVALO_MINIMO: 30, INTERVALO_MAXIMO: 180,
        AMPLITUD: 180, HORA_INICIO: 240, CONTEXTO_HORA: 240 },
      CONDICIONES: [String(nota || 'Regla manual definida por el usuario.')],
      SEGURIDAD_MINIMA: this.limitar(criterios && criterios.SEGURIDAD_MINIMA, 50, 95, 70),
      NOMBRE: String(nombre || 'Regla manual').trim(), NOTA_USUARIO: String(nota || '').trim(), MANUAL: true,
      CRITERIOS_MANUALES: {
        MIN_PICADAS: this.limitar(criterios && criterios.MIN_PICADAS, 1, 50, 1),
        MAX_PICADAS: this.limitar(criterios && criterios.MAX_PICADAS, 1, 50, 20),
        INTERVALO_ERROR_MAX: this.limitar(criterios && criterios.INTERVALO_ERROR_MAX, 1, 720, 90)
      }
    };
    return {
      ID: String(Date.now()) + '-' + Math.floor(Math.random() * 100000), CLIENTE: String(cliente || ''),
      CODIGO: '', NOMBRE: 'Regla escrita por el usuario', FECHA: '', CREADO: new Date().toISOString(),
      PATRON: String(patron || 'DUPLICADO'), EXPLICACION: regla.NOTA_USUARIO || 'Regla manual.',
      ACCION: regla.NOTA_USUARIO || 'Buscar el patron indicado.', ANTES: [], DESPUES: [], REGLA: regla,
      MOSTRADO: false, CONFIANZA: 25, CONFIRMACIONES: 1
    };
  }

  /** Guarda solo una propuesta aceptada expresamente por el usuario. */
  static guardarPropuesta(caso: CasoAprendizajeIA): CasoAprendizajeIA {
    caso.APROBADA_MANUALMENTE = true;
    const casos = this.obtener(caso.CLIENTE);
    const firma = this.firma(caso);
    const indice = casos.findIndex(item => this.firma(item) === firma);
    if (indice >= 0) {
      const anterior = casos[indice];
      caso.ID = anterior.ID;
      caso.SERVIDOR_ID = anterior.SERVIDOR_ID;
      caso.CONFIRMACIONES = Number(anterior.CONFIRMACIONES || 1) + 1;
      caso.CONFIANZA = Math.min(99, Math.round((Number(caso.CONFIRMACIONES) * 2 * 100) /
        (Number(caso.CONFIRMACIONES) * 2 + 6)));
      caso.MOSTRADO = false;
      casos.splice(indice, 1);
    }
    casos.unshift(caso);
    this.guardar(caso.CLIENTE, casos.slice(0, 50));
    return caso;
  }

  static registrar(cliente: string, codigo: string, nombre: string, fecha: string,
    antes: any[], despues: any[], incidencia?: any, contexto?: ContextoJornadaIA): CasoAprendizajeIA | null {
    return this.proponer(cliente, codigo, nombre, fecha, antes, despues, incidencia, contexto);
  }

  static pendiente(cliente: string): CasoAprendizajeIA | null {
    const casos = this.obtener(cliente);
    for (let i = 0; i < casos.length; i++) {
      if (casos[i].APROBADA_MANUALMENTE === true && !casos[i].MOSTRADO) { return casos[i]; }
    }
    return null;
  }

  static marcarMostrado(cliente: string, id: string): void {
    const casos = this.obtener(cliente);
    casos.forEach(caso => { if (caso.ID === id) { caso.MOSTRADO = true; } });
    this.guardar(cliente, casos);
  }

  static actualizarServidor(cliente: string, id: string, respuesta: any): void {
    const casos = this.obtener(cliente);
    casos.forEach(caso => {
      if (caso.ID !== id) { return; }
      if (respuesta && respuesta.ID != null) { caso.SERVIDOR_ID = Number(respuesta.ID); }
      if (respuesta && respuesta.CONFIRMACIONES != null) { caso.CONFIRMACIONES = Number(respuesta.CONFIRMACIONES); }
      if (respuesta && respuesta.CONFIANZA != null) { caso.CONFIANZA = Number(respuesta.CONFIANZA); }
    });
    this.guardar(cliente, casos);
  }

  static registrarResultado(cliente: string, id: string, acierto: boolean): void {
    const casos = this.obtener(cliente);
    casos.forEach(caso => {
      if (caso.ID !== id) { return; }
      if (acierto) { caso.ACIERTOS = Number(caso.ACIERTOS || 0) + 1; }
      else { caso.FALLOS = Number(caso.FALLOS || 0) + 1; }
      const evidencia = Number(caso.CONFIRMACIONES || 1) * 2 + Number(caso.ACIERTOS || 0) * 3;
      const penalizacion = Number(caso.FALLOS || 0) * 5;
      caso.CONFIANZA = Math.min(99, Math.round(evidencia * 100 / Math.max(1, evidencia + penalizacion + 6)));
    });
    this.guardar(cliente, casos);
  }

  static actualizarRegla(cliente: string, id: string, edicion: any): CasoAprendizajeIA | null {
    const casos = this.obtener(cliente);
    let actualizada: CasoAprendizajeIA | null = null;
    casos.forEach(caso => {
      if (caso.ID !== id || !caso.REGLA) { return; }
      caso.REGLA.NOMBRE = String(edicion.NOMBRE || caso.REGLA.NOMBRE || '').trim();
      caso.REGLA.NOTA_USUARIO = String(edicion.NOTA_USUARIO || '').trim();
      caso.REGLA.MANUAL = true;
      caso.REGLA.SEGURIDAD_MINIMA = this.limitar(edicion.SEGURIDAD_MINIMA, 50, 95,
        Number(caso.REGLA.SEGURIDAD_MINIMA || 70));
      const t = caso.REGLA.TOLERANCIAS;
      t.INTERVALO_PORCENTAJE = this.limitar(edicion.INTERVALO_PORCENTAJE, 5, 100, t.INTERVALO_PORCENTAJE);
      t.INTERVALO_MINIMO = this.limitar(edicion.INTERVALO_MINIMO, 0, 360, t.INTERVALO_MINIMO);
      t.INTERVALO_MAXIMO = this.limitar(edicion.INTERVALO_MAXIMO, t.INTERVALO_MINIMO, 720, t.INTERVALO_MAXIMO);
      t.AMPLITUD = this.limitar(edicion.AMPLITUD, 0, 720, t.AMPLITUD);
      t.HORA_INICIO = this.limitar(edicion.HORA_INICIO, 0, 720, t.HORA_INICIO);
      t.CONTEXTO_HORA = this.limitar(edicion.CONTEXTO_HORA, 0, 720, t.CONTEXTO_HORA);
      if (caso.REGLA.CRITERIOS_MANUALES) {
        caso.REGLA.CRITERIOS_MANUALES.MIN_PICADAS = this.limitar(edicion.MIN_PICADAS, 1, 50,
          caso.REGLA.CRITERIOS_MANUALES.MIN_PICADAS);
        caso.REGLA.CRITERIOS_MANUALES.MAX_PICADAS = this.limitar(edicion.MAX_PICADAS,
          caso.REGLA.CRITERIOS_MANUALES.MIN_PICADAS, 50, caso.REGLA.CRITERIOS_MANUALES.MAX_PICADAS);
        caso.REGLA.CRITERIOS_MANUALES.INTERVALO_ERROR_MAX = this.limitar(edicion.INTERVALO_ERROR_MAX, 1, 720,
          caso.REGLA.CRITERIOS_MANUALES.INTERVALO_ERROR_MAX);
      }
      caso.ACCION = caso.REGLA.NOTA_USUARIO || caso.ACCION;
      caso.MOSTRADO = true;
      actualizada = caso;
    });
    this.guardar(cliente, casos);
    return actualizada;
  }

  static eliminarRegla(cliente: string, id: string): void {
    this.guardar(cliente, this.obtener(cliente).filter(caso => caso.ID !== id));
  }

  static comparar(caso: CasoAprendizajeIA, picadas: any[], contexto?: ContextoJornadaIA): ComparacionAprendizajeIA {
    const cumple: string[] = [];
    const noCumple: string[] = [];
    if (!caso || !caso.REGLA || !caso.REGLA.HUELLA_ANTES) {
      return { COINCIDE: false, SEGURIDAD: 0, CUMPLE: cumple, NO_CUMPLE: ['La regla no contiene la jornada completa.'] };
    }
    const esperado = caso.REGLA.HUELLA_ANTES;
    const actual = this.huella(this.normalizar(picadas));
    if (caso.REGLA.MANUAL && esperado.CANTIDAD === 0) {
      return this.compararReglaManual(caso, actual, contexto || {});
    }
    const t = caso.REGLA.TOLERANCIAS;
    let puntos = 0;
    const patron = this.compararPatron(caso, actual);
    puntos += patron.puntos;
    patron.cumple.forEach(texto => cumple.push(texto));
    patron.noCumple.forEach(texto => noCumple.push(texto));

    const diferenciaCantidad = Math.abs(actual.CANTIDAD - esperado.CANTIDAD);
    const puntosCantidad = diferenciaCantidad === 0 ? 5 : (diferenciaCantidad <= 2 ? 3 : (diferenciaCantidad <= 4 ? 1 : 0));
    puntos += puntosCantidad;
    (puntosCantidad >= 3 ? cumple : noCumple).push(puntosCantidad === 5 ?
      'Tiene el mismo numero de picadas.' :
      'La cantidad de picadas varia en ' + diferenciaCantidad + ', una diferencia ' +
        (puntosCantidad >= 3 ? 'admisible.' : 'demasiado grande.'));

    if (actual.PARIDAD === esperado.PARIDAD) {
      puntos += 5;
      cumple.push('Mantiene la misma paridad ' + actual.PARIDAD.toLowerCase() + '.');
    } else {
      noCumple.push('La paridad cambia de ' + esperado.PARIDAD.toLowerCase() + ' a ' + actual.PARIDAD.toLowerCase() + '.');
    }

    const intervalos = this.compararSeriesFlexible(esperado.INTERVALOS, actual.INTERVALOS,
      t.INTERVALO_PORCENTAJE, t.INTERVALO_MINIMO, t.INTERVALO_MAXIMO);
    puntos += Math.round(15 * intervalos.proporcion);
    (intervalos.proporcion >= 0.55 ? cumple : noCumple).push(
      Math.round(intervalos.proporcion * 100) + '% de semejanza en los intervalos de la jornada; no se exigen las mismas horas.');

    const amplitudCompatible = Math.abs(actual.AMPLITUD - esperado.AMPLITUD) <= t.AMPLITUD;
    if (amplitudCompatible) { puntos += 5; cumple.push('La duracion total es semejante.'); }
    else { noCumple.push('La duracion total es muy diferente.'); }
    const inicioCompatible = actual.INICIO !== null && esperado.INICIO !== null &&
      Math.abs((actual.INICIO as number) - (esperado.INICIO as number)) <= t.HORA_INICIO;
    if (inicioCompatible) { puntos += 5; cumple.push('El inicio esta dentro del margen flexible del turno.'); }
    else { noCumple.push('El inicio se aleja del horario de referencia.'); }

    const contextoActual = contexto || {};
    const anterior = this.compararContexto(caso.REGLA.CONTEXTO.ANTERIOR,
      this.huella(this.normalizar(contextoActual.ANTERIOR || [])), t.CONTEXTO_HORA);
    const siguiente = this.compararContexto(caso.REGLA.CONTEXTO.SIGUIENTE,
      this.huella(this.normalizar(contextoActual.SIGUIENTE || [])), t.CONTEXTO_HORA);
    const aplicables = (anterior.aplicable ? 1 : 0) + (siguiente.aplicable ? 1 : 0);
    const sumaContexto = (anterior.aplicable ? anterior.proporcion : 0) +
      (siguiente.aplicable ? siguiente.proporcion : 0);
    if (aplicables === 0) {
      puntos += 20;
      cumple.push('La regla no depende de datos de dias contiguos.');
    } else {
      puntos += Math.round(20 * sumaContexto / aplicables);
      if (anterior.aplicable) {
        (anterior.proporcion >= 0.55 ? cumple : noCumple).push('Dia anterior: ' +
          Math.round(anterior.proporcion * 100) + '% de semejanza con el horario habitual.');
      }
      if (siguiente.aplicable) {
        (siguiente.proporcion >= 0.55 ? cumple : noCumple).push('Dia siguiente: ' +
          Math.round(siguiente.proporcion * 100) + '% de semejanza con el horario habitual.');
      }
    }

    const seguridad = Math.max(0, Math.min(100, Math.round(puntos)));
    const minimo = Math.max(50, Math.min(95, Number(caso.REGLA.SEGURIDAD_MINIMA || 70)));
    return { COINCIDE: patron.compatible && seguridad >= minimo, SEGURIDAD: seguridad, CUMPLE: cumple, NO_CUMPLE: noCumple };
  }

  static coincide(caso: CasoAprendizajeIA, picadas: any[], contexto?: ContextoJornadaIA): boolean {
    return this.comparar(caso, picadas, contexto).COINCIDE;
  }

  static describirRegla(caso: CasoAprendizajeIA): string[] {
    return caso && caso.REGLA ? caso.REGLA.CONDICIONES.slice() : [];
  }

  private static crearRegla(antes: PicadaAprendizajeIA[], despues: PicadaAprendizajeIA[], contexto: ContextoJornadaIA): ReglaAprendizajeIA {
    const huellaAntes = this.huella(antes);
    const huellaDespues = this.huella(despues);
    const cambios = this.cambios(antes, despues);
    const anterior = this.huella(this.normalizar(contexto.ANTERIOR || []));
    const siguiente = this.huella(this.normalizar(contexto.SIGUIENTE || []));
    const condiciones = [
      'Jornada completa: ' + huellaAntes.CANTIDAD + ' picadas (' + huellaAntes.PARIDAD.toLowerCase() + ').',
      'Secuencia original: ' + (huellaAntes.SECUENCIA.join(' - ') || 'sin picadas') + '.',
      'Horas originales: ' + (antes.map(p => p.HORA).join(' - ') || 'sin horas') + '.',
      'Intervalos: ' + (huellaAntes.INTERVALOS.map(this.formatearMinutos).join(' - ') || 'ninguno') + '.',
      'Duracion entre primera y ultima: ' + this.formatearMinutos(huellaAntes.AMPLITUD) + '.',
      'Posiciones con sentido repetido: ' + (huellaAntes.REPETIDOS.map(p => String(p + 1)).join(', ') || 'ninguna') + '.',
      'Correccion exacta: ' + this.describirCambios(cambios) + '.',
      'Resultado correcto: ' + (despues.map(p => p.SENTIDO + ' ' + p.HORA).join(' - ') || 'sin picadas') + '.',
      'Dia anterior: ' + this.resumenHuella(anterior) + '.',
      'Dia siguiente: ' + this.resumenHuella(siguiente) + '.'
    ];
    return {
      VERSION: 3, HUELLA_ANTES: huellaAntes, HUELLA_DESPUES: huellaDespues, CAMBIOS: cambios,
      CONTEXTO: { ANTERIOR: anterior, SIGUIENTE: siguiente },
      TOLERANCIAS: { INTERVALO_PORCENTAJE: 45, INTERVALO_MINIMO: 30, INTERVALO_MAXIMO: 180,
        AMPLITUD: 180, HORA_INICIO: 240, CONTEXTO_HORA: 240 },
      CONDICIONES: condiciones, SEGURIDAD_MINIMA: 70,
      NOMBRE: 'Regla aprendida de ' + this.describirCambios(cambios)
    };
  }

  private static huella(picadas: PicadaAprendizajeIA[]): HuellaJornadaIA {
    const minutos = picadas.map(p => this.minutos(p.HORA) as number);
    const intervalos: number[] = []; const repetidos: number[] = [];
    const tramos: number[] = []; const pausas: number[] = [];
    for (let i = 1; i < picadas.length; i++) {
      const diferencia = Math.max(0, minutos[i] - minutos[i - 1]);
      intervalos.push(diferencia);
      if (picadas[i].SENTIDO === picadas[i - 1].SENTIDO) { repetidos.push(i); }
      if (picadas[i - 1].SENTIDO === 'ENT' && picadas[i].SENTIDO === 'SAL') { tramos.push(diferencia); }
      if (picadas[i - 1].SENTIDO === 'SAL' && picadas[i].SENTIDO === 'ENT') { pausas.push(diferencia); }
    }
    return {
      CANTIDAD: picadas.length, PARIDAD: picadas.length % 2 === 0 ? 'PAR' : 'IMPAR',
      SECUENCIA: picadas.map(p => p.SENTIDO), MINUTOS: minutos, INTERVALOS: intervalos,
      REPETIDOS: repetidos, ALTERNA: repetidos.length === 0,
      INICIO: minutos.length ? minutos[0] : null, FIN: minutos.length ? minutos[minutos.length - 1] : null,
      AMPLITUD: minutos.length > 1 ? Math.max(0, minutos[minutos.length - 1] - minutos[0]) : 0,
      TRAMOS_TRABAJADOS: tramos, PAUSAS: pausas
    };
  }

  private static cambios(antes: PicadaAprendizajeIA[], despues: PicadaAprendizajeIA[]): CambioAprendizajeIA[] {
    const resultado: CambioAprendizajeIA[] = [];
    const porIdAntes: any = {}; const porIdDespues: any = {};
    antes.forEach((p, i) => porIdAntes[p.ID] = { p, i });
    despues.forEach((p, i) => porIdDespues[p.ID] = { p, i });
    antes.forEach((p, i) => {
      const actual = porIdDespues[p.ID];
      if (!actual) {
        resultado.push({ TIPO: 'ELIMINADA', POSICION_ANTES: i, POSICION_DESPUES: -1, ANTES: p,
          INTERVALO_ANTERIOR: i > 0 ? (this.minutos(p.HORA) as number) - (this.minutos(antes[i - 1].HORA) as number) : null,
          INTERVALO_SIGUIENTE: i + 1 < antes.length ? (this.minutos(antes[i + 1].HORA) as number) - (this.minutos(p.HORA) as number) : null });
      } else if (p.HORA !== actual.p.HORA || p.SENTIDO !== actual.p.SENTIDO || p.OBRA !== actual.p.OBRA) {
        resultado.push({ TIPO: p.SENTIDO !== actual.p.SENTIDO ? 'SENTIDO_CAMBIADO' :
          (p.HORA !== actual.p.HORA ? 'HORA_CAMBIADA' : 'LUGAR_CAMBIADO'),
          POSICION_ANTES: i, POSICION_DESPUES: actual.i, ANTES: p, DESPUES: actual.p });
      }
    });
    despues.forEach((p, i) => {
      if (!porIdAntes[p.ID]) { resultado.push({ TIPO: 'ANADIDA', POSICION_ANTES: -1, POSICION_DESPUES: i, DESPUES: p }); }
    });
    return resultado;
  }

  private static interpretar(regla: ReglaAprendizajeIA): any {
    const cambios = regla.CAMBIOS; const antes = regla.HUELLA_ANTES;
    let patron = 'CORRECCION_SECUENCIA';
    if (cambios.some(c => c.TIPO === 'ANADIDA' && c.DESPUES && c.DESPUES.SENTIDO === 'SAL') &&
      antes.PARIDAD === 'IMPAR' && antes.SECUENCIA[antes.SECUENCIA.length - 1] === 'ENT') { patron = 'FALTA_SALIDA'; }
    else if (cambios.some(c => c.TIPO === 'ANADIDA' && c.DESPUES && c.DESPUES.SENTIDO === 'ENT') &&
      antes.PARIDAD === 'IMPAR' && antes.SECUENCIA[0] === 'SAL') { patron = 'FALTA_ENTRADA'; }
    else if (cambios.some(c => c.TIPO === 'ELIMINADA')) { patron = 'DUPLICADO'; }
    else if (cambios.some(c => c.TIPO === 'SENTIDO_CAMBIADO')) { patron = 'CAMBIO_SENTIDO'; }
    else if (cambios.some(c => c.TIPO === 'HORA_CAMBIADA')) { patron = 'AJUSTE_HORA'; }
    return {
      patron,
      explicacion: 'He comparado la jornada completa antes y despues: ' + this.describirCambios(cambios) + '.',
      accion: 'Buscare el mismo tipo de error en jornadas semejantes, aunque cambien las horas o existan mas tramos, usando tambien los dias anterior y siguiente.'
    };
  }

  private static describirCambios(cambios: CambioAprendizajeIA[]): string {
    if (!cambios.length) { return 'no hay cambios estructurales'; }
    return cambios.map(c => {
      const posicion = (c.POSICION_ANTES >= 0 ? c.POSICION_ANTES : c.POSICION_DESPUES) + 1;
      if (c.TIPO === 'ELIMINADA' && c.ANTES) { return 'eliminar la picada ' + posicion + ' (' + c.ANTES.SENTIDO + ' ' + c.ANTES.HORA + ')'; }
      if (c.TIPO === 'ANADIDA' && c.DESPUES) { return 'anadir en la posicion ' + posicion + ' ' + c.DESPUES.SENTIDO + ' ' + c.DESPUES.HORA; }
      if (c.ANTES && c.DESPUES) { return 'cambiar la picada ' + posicion + ' de ' + c.ANTES.SENTIDO + ' ' + c.ANTES.HORA +
        ' a ' + c.DESPUES.SENTIDO + ' ' + c.DESPUES.HORA; }
      return c.TIPO.toLowerCase();
    }).join('; ');
  }

  private static compararReglaManual(caso: CasoAprendizajeIA, actual: HuellaJornadaIA,
    contexto: ContextoJornadaIA): ComparacionAprendizajeIA {
    const cumple: string[] = []; const noCumple: string[] = [];
    const criterios = caso.REGLA.CRITERIOS_MANUALES || { MIN_PICADAS: 1, MAX_PICADAS: 20, INTERVALO_ERROR_MAX: 90 };
    let puntos = 0; let patronCompatible = false;
    if (caso.PATRON === 'FALTA_SALIDA') {
      patronCompatible = actual.CANTIDAD % 2 === 1 && actual.SECUENCIA[actual.SECUENCIA.length - 1] === 'ENT';
    } else if (caso.PATRON === 'FALTA_ENTRADA') {
      patronCompatible = actual.CANTIDAD % 2 === 1 && actual.SECUENCIA[0] === 'SAL';
    } else if (caso.PATRON === 'DUPLICADO' || caso.PATRON === 'CAMBIO_SENTIDO') {
      patronCompatible = actual.REPETIDOS.length > 0;
    }
    if (patronCompatible) { puntos += 60; cumple.push('Cumple el patron manual ' + caso.PATRON.toLowerCase().replace(/_/g, ' ') + '.'); }
    else { noCumple.push('No cumple el patron estructural escrito en la regla.'); }

    const cantidadCompatible = actual.CANTIDAD >= criterios.MIN_PICADAS && actual.CANTIDAD <= criterios.MAX_PICADAS;
    if (cantidadCompatible) { puntos += 15; cumple.push('Cantidad dentro de la horquilla: ' + actual.CANTIDAD + ' picadas.'); }
    else { noCumple.push('Cantidad fuera de la horquilla ' + criterios.MIN_PICADAS + '-' + criterios.MAX_PICADAS + '.'); }

    let intervaloCompatible = true;
    if (caso.PATRON === 'DUPLICADO' || caso.PATRON === 'CAMBIO_SENTIDO') {
      intervaloCompatible = actual.REPETIDOS.some(posicion =>
        actual.INTERVALOS[posicion - 1] != null && actual.INTERVALOS[posicion - 1] <= criterios.INTERVALO_ERROR_MAX);
    }
    if (intervaloCompatible) { puntos += 15; cumple.push('El intervalo sospechoso entra en la horquilla de minutos.'); }
    else { noCumple.push('Las picadas repetidas estan separadas por mas de ' + criterios.INTERVALO_ERROR_MAX + ' minutos.'); }

    const dias = [contexto.ANTERIOR || [], contexto.SIGUIENTE || []].map(lista => this.huella(this.normalizar(lista)));
    const disponibles = dias.filter(dia => dia.CANTIDAD > 0 && dia.INICIO !== null && actual.INICIO !== null);
    if (!disponibles.length) {
      puntos += 5;
      cumple.push('No hay dias contiguos suficientes; el contexto no suma toda su puntuacion.');
    } else {
      const compatibles = disponibles.filter(dia => Math.abs((dia.INICIO as number) - (actual.INICIO as number)) <=
        caso.REGLA.TOLERANCIAS.CONTEXTO_HORA).length;
      puntos += Math.round(10 * compatibles / disponibles.length);
      (compatibles > 0 ? cumple : noCumple).push(compatibles + ' de ' + disponibles.length +
        ' dias contiguos tienen un inicio semejante.');
    }
    const seguridad = Math.max(0, Math.min(100, Math.round(puntos)));
    const minimo = Math.max(50, Math.min(95, Number(caso.REGLA.SEGURIDAD_MINIMA || 70)));
    return { COINCIDE: patronCompatible && seguridad >= minimo, SEGURIDAD: seguridad, CUMPLE: cumple, NO_CUMPLE: noCumple };
  }

  private static compararSeries(esperado: number[], actual: number[], porcentaje: number, minimo: number, maximo: number): any {
    if (esperado.length === 0 && actual.length === 0) { return { proporcion: 1, coinciden: 0, total: 0 }; }
    const total = Math.max(esperado.length, actual.length); let coinciden = 0;
    for (let i = 0; i < total; i++) {
      if (esperado[i] == null || actual[i] == null) { continue; }
      const margen = Math.min(maximo, Math.max(minimo, Math.round(esperado[i] * porcentaje / 100)));
      if (Math.abs(esperado[i] - actual[i]) <= margen) { coinciden++; }
    }
    return { proporcion: total ? coinciden / total : 0, coinciden, total };
  }

  private static compararSeriesFlexible(esperado: number[], actual: number[], porcentaje: number, minimo: number, maximo: number): any {
    if (!esperado.length && !actual.length) { return { proporcion: 1, coinciden: 0, total: 0 }; }
    if (!esperado.length || !actual.length) { return { proporcion: 0, coinciden: 0, total: Math.max(esperado.length, actual.length) }; }
    const disponibles = actual.slice();
    let coinciden = 0;
    esperado.forEach(valor => {
      let mejor = -1; let distancia = Number.MAX_VALUE;
      disponibles.forEach((candidato, indice) => {
        const actualDistancia = Math.abs(valor - candidato);
        if (actualDistancia < distancia) { distancia = actualDistancia; mejor = indice; }
      });
      const margen = Math.min(maximo, Math.max(minimo, Math.round(Math.max(1, valor) * porcentaje / 100)));
      if (mejor >= 0 && distancia <= margen) { coinciden++; disponibles.splice(mejor, 1); }
    });
    return { proporcion: coinciden / Math.max(esperado.length, actual.length),
      coinciden, total: Math.max(esperado.length, actual.length) };
  }

  private static compararPatron(caso: CasoAprendizajeIA, actual: HuellaJornadaIA): any {
    const cumple: string[] = []; const noCumple: string[] = [];
    const cambio = caso.REGLA.CAMBIOS && caso.REGLA.CAMBIOS.length ? caso.REGLA.CAMBIOS[0] : null;
    let compatible = false; let puntos = 0;
    if (caso.PATRON === 'FALTA_SALIDA') {
      compatible = actual.CANTIDAD % 2 === 1 && actual.SECUENCIA[actual.SECUENCIA.length - 1] === 'ENT';
    } else if (caso.PATRON === 'FALTA_ENTRADA') {
      compatible = actual.CANTIDAD % 2 === 1 && actual.SECUENCIA[0] === 'SAL';
    } else if (caso.PATRON === 'DUPLICADO' || caso.PATRON === 'CAMBIO_SENTIDO') {
      const sentido = cambio && cambio.ANTES && cambio.ANTES.SENTIDO;
      compatible = actual.REPETIDOS.some(posicion => !sentido || actual.SECUENCIA[posicion] === sentido);
    } else if (caso.PATRON === 'AJUSTE_HORA') {
      compatible = actual.CANTIDAD >= 2;
    } else {
      const semejanza = this.semejanzaSecuencia(caso.REGLA.HUELLA_ANTES.SECUENCIA, actual.SECUENCIA);
      compatible = semejanza >= 0.6;
    }
    if (compatible) {
      puntos += 25;
      cumple.push('Se repite el mismo tipo de error: ' + caso.PATRON.toLowerCase().replace(/_/g, ' ') + '.');
    } else {
      noCumple.push('No aparece el mismo tipo de error estructural.');
    }

    let local = 0;
    if (compatible && (caso.PATRON === 'DUPLICADO' || caso.PATRON === 'CAMBIO_SENTIDO') && cambio) {
      const referencia = [cambio.INTERVALO_ANTERIOR, cambio.INTERVALO_SIGUIENTE]
        .filter(valor => valor !== null && valor !== undefined) as number[];
      let mejor = 0;
      actual.REPETIDOS.forEach(posicion => {
        const alrededor: number[] = [];
        if (actual.INTERVALOS[posicion - 1] != null) { alrededor.push(actual.INTERVALOS[posicion - 1]); }
        if (actual.INTERVALOS[posicion] != null) { alrededor.push(actual.INTERVALOS[posicion]); }
        mejor = Math.max(mejor, this.compararSeriesFlexible(referencia, alrededor, 60, 45, 240).proporcion);
      });
      local = Math.round(20 * mejor);
      (mejor >= 0.5 ? cumple : noCumple).push(Math.round(mejor * 100) +
        '% de semejanza en los minutos alrededor de la picada que se corrigio.');
    } else if (compatible) {
      local = 20;
      cumple.push('La posicion relativa del fallo es compatible con la regla.');
    }
    puntos += local;
    return { compatible, puntos, cumple, noCumple };
  }

  private static semejanzaSecuencia(a: string[], b: string[]): number {
    if (!a.length && !b.length) { return 1; }
    const tabla: number[][] = [];
    for (let i = 0; i <= a.length; i++) {
      tabla[i] = [];
      for (let j = 0; j <= b.length; j++) { tabla[i][j] = 0; }
    }
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        tabla[i][j] = a[i - 1] === b[j - 1] ? tabla[i - 1][j - 1] + 1 : Math.max(tabla[i - 1][j], tabla[i][j - 1]);
      }
    }
    return tabla[a.length][b.length] / Math.max(a.length, b.length);
  }

  private static compararContexto(esperado: HuellaJornadaIA, actual: HuellaJornadaIA, tolerancia: number): any {
    if (!esperado || esperado.CANTIDAD === 0) { return { aplicable: false, coincide: true, proporcion: 1 }; }
    if (!actual || actual.CANTIDAD === 0) { return { aplicable: true, coincide: false, proporcion: 0 }; }
    let puntos = 0;
    if (Math.abs(actual.CANTIDAD - esperado.CANTIDAD) <= 2) { puntos += 0.15; }
    if (actual.INICIO !== null && esperado.INICIO !== null &&
      Math.abs((actual.INICIO as number) - (esperado.INICIO as number)) <= tolerancia) { puntos += 0.25; }
    if (actual.FIN !== null && esperado.FIN !== null &&
      Math.abs((actual.FIN as number) - (esperado.FIN as number)) <= tolerancia) { puntos += 0.25; }
    if (Math.abs(actual.AMPLITUD - esperado.AMPLITUD) <= Math.max(120, Math.round(tolerancia * 0.75))) { puntos += 0.25; }
    if (actual.PARIDAD === esperado.PARIDAD || (actual.ALTERNA && esperado.ALTERNA)) { puntos += 0.10; }
    return { aplicable: true, coincide: puntos >= 0.55, proporcion: puntos };
  }

  private static resumenHuella(huella: HuellaJornadaIA): string {
    if (!huella.CANTIDAD) { return 'sin datos disponibles'; }
    return huella.CANTIDAD + ' picadas, ' + huella.SECUENCIA.join(' - ') + ', de ' +
      this.formatearHora(huella.INICIO) + ' a ' + this.formatearHora(huella.FIN);
  }

  private static formatearHora(minutos: number | null): string {
    if (minutos === null) { return '--:--'; }
    return ('0' + Math.floor(minutos / 60)).slice(-2) + ':' + ('0' + (minutos % 60)).slice(-2);
  }

  private static formatearMinutos(valor: number): string {
    const horas = Math.floor(Math.abs(valor) / 60); const minutos = Math.abs(valor) % 60;
    return ('0' + horas).slice(-2) + ':' + ('0' + minutos).slice(-2);
  }

  private static minutos(hora: any): number | null {
    const partes = /^(\d{1,2}):(\d{2})$/.exec(String(hora || '').trim());
    if (!partes) { return null; }
    const h = Number(partes[1]); const m = Number(partes[2]);
    return h >= 0 && h <= 24 && m >= 0 && m < 60 && !(h === 24 && m !== 0) ? h * 60 + m : null;
  }

  private static limitar(valor: any, minimo: number, maximo: number, defecto: number): number {
    const numero = Number(valor);
    return isFinite(numero) ? Math.max(minimo, Math.min(maximo, Math.round(numero))) : defecto;
  }

  private static firma(caso: CasoAprendizajeIA): string {
    if (!caso || !caso.REGLA) { return ''; }
    const simplificarPicada = (p: PicadaAprendizajeIA | undefined) => p ?
      { HORA: p.HORA, SENTIDO: p.SENTIDO, OBRA: p.OBRA } : null;
    const cambios = (caso.REGLA.CAMBIOS || []).map(c => ({
      TIPO: c.TIPO, POSICION_ANTES: c.POSICION_ANTES, POSICION_DESPUES: c.POSICION_DESPUES,
      ANTES: simplificarPicada(c.ANTES), DESPUES: simplificarPicada(c.DESPUES),
      INTERVALO_ANTERIOR: c.INTERVALO_ANTERIOR, INTERVALO_SIGUIENTE: c.INTERVALO_SIGUIENTE
    }));
    return JSON.stringify({ PATRON: caso.PATRON, VERSION: caso.REGLA.VERSION,
      HUELLA_ANTES: caso.REGLA.HUELLA_ANTES, HUELLA_DESPUES: caso.REGLA.HUELLA_DESPUES,
      CAMBIOS: cambios, CONTEXTO: caso.REGLA.CONTEXTO, TOLERANCIAS: caso.REGLA.TOLERANCIAS,
      SEGURIDAD_MINIMA: caso.REGLA.SEGURIDAD_MINIMA, MANUAL: caso.REGLA.MANUAL,
      CRITERIOS_MANUALES: caso.REGLA.CRITERIOS_MANUALES });
  }

  private static sentido(valor: any): string {
    const texto = String(valor || '').trim().toUpperCase();
    if (texto === 'ENT' || texto === 'ENTRA' || texto === 'ENTRADA') { return 'ENT'; }
    if (texto === 'SAL' || texto === 'SALE' || texto === 'SALIDA') { return 'SAL'; }
    return '';
  }

  private static iguales(a: PicadaAprendizajeIA[], b: PicadaAprendizajeIA[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private static obtener(cliente: string): CasoAprendizajeIA[] {
    if (typeof localStorage === 'undefined') { return []; }
    try {
      const valor = JSON.parse(localStorage.getItem(this.clave(cliente)) || '[]');
      return Array.isArray(valor) ? valor : [];
    } catch (error) { return []; }
  }

  private static guardar(cliente: string, casos: CasoAprendizajeIA[]): void {
    if (typeof localStorage === 'undefined') { return; }
    try { localStorage.setItem(this.clave(cliente), JSON.stringify(casos || [])); }
    catch (error) { /* El aprendizaje no debe bloquear la gestion de picadas. */ }
  }
}



// WEBPACK FOOTER //
// ./src/providers/ahorita-aprendizaje.ts